import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken/Action';
import { DataSource } from '~/Api/DataSource';
import { ReferencedProductType } from '~/Api/SiPriceReferencing/MaxPrice';
import { ProductUpdate, SiMaxPrice, ValidationResult } from '~/Api/User/ProductGroup/SiMaxPrice';
import { ProductPrice } from '~/Product';

import ActionTypes from './ActionTypes';

export interface SiMaxPriceState {
	request: Promise<any>;
	isFetching: boolean;
	siMaxPrice: SiMaxPrice | null;
}

type UpdateState =
	| {
			name: 'scheduled';
			timeoutId: NodeJS.Timeout;
			dosageStrength: number | null;
			historicProductDataViewDate: Date | null;
			packageSize: number | null;
			referenceBasketVersion: string | null;
			referencedProductType: string | null;
			products: ProductUpdate[];
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	request: Promise<any>;
	isFetching: boolean;
	validationResult: ValidationResult | null;
}

export interface State {
	byId: { [key in string]: SiMaxPriceState };
	update: Update | null;
	export: Export | null;
	dosageStrength: string | null;
	referencedProductType: ReferencedProductType | null;
	referenceBasketVersion: string | null;
	productTypes: {
		isFetching: boolean;
		request: Promise<any>;
		types: ReferencedProductType[] | null;
	} | null;
}

type Action =
	| { type: ActionTypes.request; request: Promise<any>; groupId: string }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			groupId: string;
			siMaxPrice: SiMaxPrice | null;
	  }
	| {
			type: ActionTypes.scheduleUpdate;
			groupId: string;
			dosageStrength: number | null;
			timeoutId: NodeJS.Timeout;
			packageSize: number | null;
			referenceBasketVersion: string | null;
			referencedProductType: string;
			basketVersion: string;
			historicProductDataViewDate: Date | null;
			products: ProductUpdate[];
	  }
	| { type: ActionTypes.requestUpdate; groupId: string; request: Promise<any> }
	| {
			type: ActionTypes.receiveUpdate;
			groupId: string;
			request: Promise<any>;
			siMaxPrice: SiMaxPrice | null;
	  }
	| {
			type: ActionTypes.requestXlsCreate;
			groupId: string;
			request: Promise<any>;
	  }
	| {
			type: ActionTypes.receiveXlsCreate;
			request: Promise<any>;
			validationResult: ValidationResult | null;
	  }
	| { type: ActionTypes.requestProductTypes; request: Promise<any> }
	| {
			type: ActionTypes.receiveProductTypes;
			request: Promise<any>;
			types: ReferencedProductType[] | null;
	  }
	| { type: ActionTypes.prune; groupId: string };

function createState(): State {
	return {
		byId: {},
		update: null,
		export: null,
		referenceBasketVersion: null,
		dosageStrength: null,
		referencedProductType: null,
		productTypes: null,
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionTypes.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						siMaxPrice: state.byId[action.groupId]?.siMaxPrice ?? null,
					},
				},
			};

		case ActionTypes.receive: {
			const siMaxPrice = state.byId[action.groupId];
			if (siMaxPrice?.request !== action.request) {
				return state;
			}

			if (!action.siMaxPrice) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...siMaxPrice,
						isFetching: false,
						siMaxPrice: action.siMaxPrice,
					},
				},
			};
		}

		case ActionTypes.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionTypes.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						referenceBasketVersion: action.referenceBasketVersion,
						referencedProductType: action.referencedProductType,
						packageSize: action.packageSize,
						dosageStrength: action.dosageStrength,
						historicProductDataViewDate: action.historicProductDataViewDate,
						products: action.products,
					},
				},
			};
		}

		case ActionTypes.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionTypes.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState?.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			const siMaxPriceState = byId[action.groupId];

			if (action.siMaxPrice && siMaxPriceState !== undefined) {
				byId[action.groupId] = {
					...siMaxPriceState,
					siMaxPrice: action.siMaxPrice,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.siMaxPrice ? { name: 'updated' } : { name: 'failed' },
				},
				export: null,
			};
		}

		case ActionTypes.requestXlsCreate:
			return {
				...state,
				export: {
					groupId: action.groupId,
					request: action.request,
					isFetching: true,
					validationResult: null,
				},
			};

		case ActionTypes.receiveXlsCreate:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case ActionTypes.requestProductTypes:
			return {
				...state,
				productTypes: {
					isFetching: true,
					request: action.request,
					types: null,
				},
			};

		case ActionTypes.receiveProductTypes:
			if (state.productTypes?.request !== action.request) {
				return state;
			}

			return {
				...state,
				productTypes:
					action.types !== null
						? {
								...state.productTypes,
								isFetching: true,
								request: action.request,
								types: action.types,
						  }
						: null,
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectSiMaxPrice(state: State, groupId: string): SiMaxPriceState | null {
	return state.byId[groupId];
}

export function selectExport(state: State, groupId: string): Export | null {
	return state.export?.groupId === groupId ? state.export : null;
}

export function selectComparativeDataSources(siMaxPrice: SiMaxPrice): DataSource[][] {
	if (siMaxPrice.finalCalculation.comparativePrice === null) {
		return [];
	}
	const dataSources = siMaxPrice.finalCalculation.comparativePrice.fromProducts.reduce(
		(dataSources: { [key: string]: DataSource[] }, product) => {
			const countryCode = product.dataSource.country.alpha2;
			if (dataSources[countryCode] === undefined) {
				dataSources[countryCode] = [product.dataSource];
			} else {
				dataSources[countryCode].push(product.dataSource);
			}
			return dataSources;
		},
		{}
	);
	return Object.values(dataSources);
}

export function selectSlovenianExFactoryPrice(siMaxPrice: SiMaxPrice): ProductPrice | null {
	let exFactoryPrice = null;
	siMaxPrice.products.forEach((product) => {
		if (product.product.dataset.source.country.alpha2 !== 'SI') {
			return;
		}

		const price = product.product.sourcePrices.find((price) => price.type === 'ex-factory');
		if (price) {
			exFactoryPrice = price;
		}
	});

	return exFactoryPrice;
}
